import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment-timezone";
import { jwtDecode } from "jwt-decode";
import API_BASE_URL from "../../config/api";
import {
  Typography,
  CircularProgress,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import Autocomplete from "@mui/material/Autocomplete";
import { createFilterOptions } from "@mui/material/Autocomplete";
import Tooltip from "@mui/material/Tooltip";
import {
  Send as SendIcon,
  Info as InfoIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import axios from "axios";
import {
  fetchRowData,
  fetchComments,
  addComment,
  addCommentWithNotification,
  updateField,
  logHistory,
  saveHistory,
  fetchPowodWpisaniaOptions,
  saveReminderStatus,
  fetchUsers,
  mapPodpisanyToUsername,
} from "./RejestrFunctions"; // Import funkcji z pliku apiService.js
import { usePageStyles } from "../common/pageStyles";

const BASE_URL = API_BASE_URL;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  content: {
    flexGrow: 1,
    width: "100%",
    minWidth: 0,
    overflowX: "auto",
  },
  loaderContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(10, 2),
    color: "#666",
  },
  // ---- Kafelki pól do edycji (jak w DetailPage) ----
  categoryRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
    "&:not(:last-child)": {
      borderBottom: `1px solid ${theme.palette.divider}`,
      paddingBottom: theme.spacing(2),
    },
  },
  categoryHeading: {
    fontWeight: "bold",
    marginRight: theme.spacing(2),
  },
  fieldBlocksRow: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  fieldLabel: {
    color: "#000",
    fontWeight: 500,
    textAlign: "center",
  },
  // Pole bez wartości: jasnoszary chip z czarnym napisem.
  fieldBlock: {
    padding: theme.spacing(1),
    backgroundColor: "#e0e0e0",
    border: "1px solid #bdbdbd",
    borderRadius: theme.shape.borderRadius,
    cursor: "pointer",
    transition: "background-color 0.3s",
    "&:hover": {
      backgroundColor: "#cfcfcf",
    },
  },
  // Pole z uzupełnioną wartością - zielony chip.
  fieldBlockValid: {
    backgroundColor: theme.palette.success.main,
    borderColor: theme.palette.success.main,
    "&:hover": {
      backgroundColor: theme.palette.success.dark,
    },
    "& $fieldLabel": {
      color: theme.palette.common.white,
    },
  },

  // ---- Podgląd danych wniosku (karty w 2 kolumnach, jak w DetailPage) ----
  nhStatusBar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "baseline",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2),
    background: "#e3f2fd",
    borderLeft: "5px solid #1976d2",
    borderRadius: 6,
  },
  nhStatusLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: "#555",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  nhStatusValue: {
    fontSize: 20,
    fontWeight: 700,
    color: "#0d47a1",
  },
  nhSummaryWrap: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-start",
    gap: theme.spacing(3),
  },
  nhSummaryCol: {
    flex: "1 1 340px",
    minWidth: 280,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  nhCard: {
    background: "#fff",
    border: "1px solid #e0e0e0",
    borderRadius: 8,
    padding: theme.spacing(2),
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  },
  nhCardTitle: {
    fontSize: 20,
    fontWeight: 800,
    color: "#1976d2",
    borderBottom: "2px solid #1976d2",
    paddingBottom: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  nhRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: theme.spacing(2),
    padding: theme.spacing(0.75, 0),
    borderBottom: "1px solid #f0f0f0",
    fontSize: 15,
    "&:last-child": { borderBottom: "none" },
  },
  nhRowLabel: {
    color: "#666",
    fontWeight: 600,
    flexShrink: 0,
  },
  nhRowValue: {
    color: "#212121",
    textAlign: "right",
    wordBreak: "break-word",
  },
  nhRowValueEmpty: {
    color: "#bbb",
  },
  komentarzeHeading: {
    marginTop: theme.spacing(2),
    fontSize: "30px",
    fontWeight: 450,
  },
  komentarzeBody: {
    fontSize: "18px",
    fontWeight: 300,
  },
  mailNotificationButton: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  // ---- Powiadomienia mailowe w komentarzach ----
  mailCard: {
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1),
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  },
  mailCardIncoming: {
    backgroundColor: "#fff8e1",
    border: "1px solid #ffe0b2",
    borderLeft: "6px solid #ff9800",
    "& $mailBadge": { backgroundColor: "#ff9800" },
  },
  mailCardHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    borderBottom: "1px solid rgba(0,0,0,0.08)",
  },
  mailBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    padding: "4px 12px",
    borderRadius: 16,
    color: "#fff",
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: "0.2px",
  },
  mailCardFooter: {
    marginTop: theme.spacing(1),
    fontSize: 13,
    color: "#555",
  },
  // ---- Formularz dodawania komentarza ----
  ckSaveButton: {
    "&&": {
      width: "auto",
      minWidth: 180,
      padding: theme.spacing(1.25, 3),
      borderRadius: 24,
      fontSize: 16,
      fontWeight: 700,
      textTransform: "none",
      letterSpacing: 0.3,
      background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
      color: "#fff",
      boxShadow: "0 3px 8px rgba(25,118,210,0.35)",
      transition: "box-shadow 0.2s, transform 0.1s",
    },
    "&&:hover": {
      boxShadow: "0 5px 12px rgba(25,118,210,0.45)",
      background: "linear-gradient(135deg, #1e88e5 0%, #1565c0 100%)",
    },
    "&&:active": {
      transform: "translateY(1px)",
    },
  },
  cmCard: {
    background: "#fff",
    border: "1px solid #e0e0e0",
    borderRadius: 8,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  },
  cmTitle: {
    fontSize: 20,
    fontWeight: 800,
    color: "#1976d2",
    borderBottom: "2px solid #1976d2",
    paddingBottom: theme.spacing(0.5),
    marginBottom: theme.spacing(1.5),
  },
  cmInfo: {
    padding: theme.spacing(1, 1.5),
    marginBottom: theme.spacing(2),
    background: "#e3f2fd",
    borderLeft: "4px solid #1976d2",
    borderRadius: 4,
    fontSize: 14,
    color: "#0d47a1",
  },
  cmLabel: {
    display: "block",
    marginBottom: theme.spacing(0.5),
    fontSize: 13,
    fontWeight: 600,
    color: "#666",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  cmLabelRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    "& > span": { marginBottom: 0 },
  },
  cmTextarea: {
    width: "100%",
    boxSizing: "border-box",
    padding: theme.spacing(1.25, 1.5),
    border: "1px solid #bdbdbd",
    borderRadius: 6,
    fontFamily: "inherit",
    fontSize: 15,
    resize: "vertical",
    minHeight: 100,
    maxHeight: 240,
    outline: "none",
    transition: "border-color 0.2s, box-shadow 0.2s",
    "&:focus": {
      borderColor: "#1976d2",
      boxShadow: "0 0 0 3px rgba(25,118,210,0.18)",
    },
  },
  cmFooter: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(1),
  },
  // ---- Sekcja znaczników (chipy) ----
  ckSection: {
    marginTop: theme.spacing(5),
    paddingTop: theme.spacing(3),
    borderTop: "4px solid #e3f2fd",
  },
  ckSectionHeader: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1, 2),
    fontSize: 24,
    fontWeight: 800,
    color: "#0d47a1",
    background: "#e3f2fd",
    borderLeft: "5px solid #1976d2",
    borderRadius: 6,
  },
  // Dwie kolumny kart obok siebie; na wąskim ekranie jedna.
  ckGrid: {
    columnCount: 2,
    columnGap: theme.spacing(2),
    "@media (max-width: 900px)": {
      columnCount: 1,
    },
  },
  ckCard: {
    breakInside: "avoid",
    background: "#fff",
    border: "1px solid #e0e0e0",
    borderRadius: 8,
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    marginBottom: theme.spacing(2),
  },
  ckCardHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    width: "100%",
    padding: theme.spacing(1.25, 2),
    background: "transparent",
    border: "none",
    borderBottom: "2px solid #1976d2",
    cursor: "pointer",
    textAlign: "left",
  },
  ckCardTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: 800,
    color: "#1976d2",
  },
  ckCount: {
    minWidth: 24,
    padding: "2px 8px",
    borderRadius: 12,
    background: "#2e7d32",
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    textAlign: "center",
  },
  ckArrow: {
    color: "#1976d2",
    fontSize: 16,
  },
  ckCardBody: {
    padding: theme.spacing(1.5, 2, 2),
  },
  ckSubTitle: {
    margin: theme.spacing(1, 0, 0.5),
    fontSize: 13,
    fontWeight: 600,
    color: "#666",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  ckChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  ckChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.75, 1.5),
    border: "1px solid #bdbdbd",
    borderRadius: 18,
    background: "#f5f5f5",
    color: "#424242",
    fontSize: 14,
    cursor: "pointer",
    userSelect: "none",
    transition: "background-color 0.2s, border-color 0.2s",
    "&:hover": {
      background: "#eeeeee",
      borderColor: "#9e9e9e",
    },
    "&:focus-within": {
      outline: "2px solid #1976d2",
      outlineOffset: 2,
    },
  },
  ckChipChecked: {
    background: theme.palette.success.main,
    borderColor: theme.palette.success.main,
    color: "#fff",
    fontWeight: 600,
    "&:hover": {
      background: theme.palette.success.dark,
      borderColor: theme.palette.success.dark,
    },
  },
  // Ukryty wizualnie, ale nadal fokusowalny natywny checkbox.
  ckInput: {
    position: "absolute",
    opacity: 0,
    width: 0,
    height: 0,
    margin: 0,
  },
  ckActionBar: {
    position: "sticky",
    bottom: 0,
    zIndex: 2,
    padding: theme.spacing(1.5, 2),
    background: "#fff",
    borderTop: "1px solid #e0e0e0",
    boxShadow: "0 -2px 6px rgba(0,0,0,0.08)",
  },
  // ---- Lista komentarzy ----
  ccItem: {
    background: "#fff",
    border: "1px solid #e0e0e0",
    borderLeft: "4px solid #9e9e9e",
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1),
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  },
  ccHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    borderBottom: "1px solid #f0f0f0",
  },
  ccAvatar: {
    flexShrink: 0,
    width: 34,
    height: 34,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: 16,
    fontWeight: 700,
  },
  ccAuthor: {
    fontSize: 16,
    fontWeight: 700,
    color: "#212121",
  },
  ccDate: {
    marginLeft: "auto",
    fontSize: 13,
    color: "#777",
    whiteSpace: "nowrap",
  },
  ccText: {
    fontSize: 16,
    lineHeight: 1.5,
    color: "#212121",
    whiteSpace: "pre-line",
    overflowWrap: "anywhere",
  },
  dialog: {
    padding: theme.spacing(3),
    backgroundColor: "white",
    boxShadow: theme.shadows[5],
    borderRadius: theme.shape.borderRadius,
    maxWidth: "500px",
    margin: "0 auto",
  },
  dialogTitle: {
    marginBottom: theme.spacing(2),
    textAlign: "center",
  },
  buttonGroup: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(2),
  },
}));

const categories = {
  "Informacje Podstawowe": [
    "powod_wpisania",
    "nr_MRN",
    "nr_akt",
    "sciezkaDoPliku",
    "status",
  ],
  "Szczegóły wniosku": [
    "nadawca_odbiorca",
    "odbiorca",
    "od_kogo_wezwanie",
    "podpisany",
    "rodzaj_SAD",
  ],
  "Procedura i Płatnik": ["procedura", "platnik"],
  Terminy: [
    "data_SAD",
    "data_wyslania",
    "data_odebrania",
    "data_odpowiedzi_na_pytanie",
    "data_doreczenia",
  ],
};

const wezwanieEditableFields = [
  "nr_akt",
  "sciezkaDoPliku",
  "nr_MRN",
  "nadawca_odbiorca",
  "odbiorca",
  "podpisany",
  "data_odebrania",
  "data_odpowiedzi_na_pytanie",
];

const fieldLabels = {
  powod_wpisania: "Powód wpisania",
  nr_MRN: "MRN",
  nr_akt: "Numer akt",
  nadawca_odbiorca: "Nadawca",
  odbiorca: "Odbiorca",
  od_kogo_wezwanie: "Od kogo wezwanie (Urząd/Oddział celny)",
  podpisany: "Os. Odpowiedzialna",
  rodzaj_SAD: "Rodzaj SAD",
  procedura: "Procedura",
  data_SAD: "Data SAD",
  data_wyslania: "Data wysłania",
  platnik: "Płatnik",
  data_odebrania: "Data odebrania decyzji z UC",
  data_odpowiedzi_na_pytanie: "Data odpowiedzi",
  sciezkaDoPliku: "Ścieżka",
  data_doreczenia: "Data doręczenia do Kontrahenta",
  doreczenie_nie_dotyczy: "Doręczenie nie dotyczy",
  powod_braku_doreczenia: "Powód braku daty doręczenia",
  status: "Status",
};

const platnikOptions = ["KLIENT", "WEGA"];

const statusOptions = [
  "W toku",
  "Do weryfikacji",
  "Do poprawy",
  "Do wysłania",
  "Wysłany",
  "Do poprawy na wniosek UC",
  "Wycofany",
  "Odebrany z urzędu",
  "Zakończony",
];

const normalizeMrnForComparison = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");

// const podpisanyOptions = [
//   "BRUŚ BARBARA",
//   "CIŻEWSKA JUSTYNA",
//   "CZERNIKOWSKA DOMINIKA",
//   "DOBROWOLSKA-MITRĘGA ANNA",
//   "JAKUBOWSKA MARTA",
//   "ŁOMŻA PRZEMYSŁAW",
//   "ŁOPATECKA MARTA",
//   "MAZUR SABINA",
//   "MUNIK PIOTR",
//   "NIEDBALSKI ADRIAN",
//   "NOWAK PRZEMYSŁAW",
//   "PUZYR GRZEGORZ",
//   "REDUCHA KATARZYNA",
//   "SKOLMOWSKA MAGDALENA",
//   "SZKUTNIK AGNIESZKA",
//   "WOJTAŚ MILENA",
// ];

const nhIsEmpty = (value) =>
  value === null || value === undefined || value === "";

const renderNhRow = (classes, label, value) => (
  <div key={label} className={classes.nhRow}>
    <span className={classes.nhRowLabel}>{label}</span>
    <span
      className={
        nhIsEmpty(value)
          ? `${classes.nhRowValue} ${classes.nhRowValueEmpty}`
          : classes.nhRowValue
      }
    >
      {nhIsEmpty(value) ? "—" : value}
    </span>
  </div>
);

const formatDate = (value, format = "YYYY-MM-DD") =>
  value ? moment(value).format(format) : "";

const EditWniosek1 = () => {
  const classes = useStyles();
  const pageClasses = usePageStyles();
  const mailNotificationPrefix = "[POWIADOMIENIE_MAILOWE]";
  const mrnRegex = /^[2][3-9]PL[A-Z0-9]{14,16}$/;
  const mrnValidationMessage = `Pole MRN musi zaczynać się od roku (23PL i wyżej), zawierać od 18 do 20 znaków alfanumerycznych (bez spacji i znaków specjalnych).`;
  const { id } = useParams();
  const [rowData, setRowData] = useState(null);
  const [editField, setEditField] = useState(null);
  const [editedValue, setEditedValue] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [commentsData, setCommentsData] = useState([]);
  const [commentValue, setCommentValue] = useState("");
  const [additionalRecipients, setAdditionalRecipients] = useState([]);
  const [recipientsError, setRecipientsError] = useState("");
  const [validLoginsList, setValidLoginsList] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const autocompleteRef = useRef(null);
  const [powodWpisaniaOptions, setPowodWpisaniaOptions] = useState([]);
  const [reminderSent, setReminderSent] = useState(false);
  const [usersOptions, setUsersOptions] = useState([]);

  const [legalDeadlineChecked, setLegalDeadlineChecked] = useState(false);
  const [legalDeadlineDate, setLegalDeadlineDate] = useState("");
  const [dateError, setDateError] = useState(false);
  const [pytanieZUrzedu, setPytanieZUrzedu] = useState(false);
  const [odpowiedzNaPytanie, setOdpowiedzNaPytanie] = useState(false);
  const [deliveryNotApplicable, setDeliveryNotApplicable] = useState(false);
  const [deliveryExceptionReason, setDeliveryExceptionReason] = useState("");
  const [deliveryExceptionError, setDeliveryExceptionError] = useState("");
  const [kwMatches, setKwMatches] = useState([]);
  const [isKwLookupLoading, setIsKwLookupLoading] = useState(false);
  const [isKwSelectionDialogOpen, setIsKwSelectionDialogOpen] = useState(false);

  const usersWithoutVerification = [
    "Maciej",
    "adobrowolska",
    "bbrus",
    "dczernikowska",
    "kreducha",
    "pmunik",
    "pnowak",
    "plomza",
    "sbrus",
    "gpuzyr",
    "jcizewska",
    "mskolmowska",
    "mjakubowska",
  ];

  const navigate = useNavigate();

  const token = localStorage.getItem("token");
  let username;
  let role;
  if (token) {
    const decoded = jwtDecode(token);
    username = decoded.username;
    role = decoded.role;
  }

  const fetchUsersData = async () => {
    try {
      const options = await fetchUsers();

      // Sortowanie alfabetyczne po pobraniu danych
      const sortedOptions = options.sort((a, b) =>
        a.name.localeCompare(b.name),
      );
      setUsersOptions(sortedOptions); // Ustaw posortowane opcje
    } catch (error) {
      toast.error("Błąd podczas pobierania powodów wpisania.");
    }
  };

  const getApiErrorMessage = (error, fallbackMessage) =>
    error?.response?.data?.message || fallbackMessage;

  const hasDeliveryException = (data) =>
    !!Number(data?.doreczenie_nie_dotyczy) &&
    String(data?.powod_braku_doreczenia || "").trim() !== "";

  const checkAndUpdateStatus = async (newRowData) => {
    // Sprawdź, czy status nie jest już "Wysłany" oraz czy data_wyslania jest ustawiona
    if (newRowData.status === "Do wysłania" && newRowData.data_wyslania) {
      try {
        await updateField(id, { status: "Wysłany" });
        toast.success("Status został zaktualizowany na Wysłany");
        fetchRowData(id).then(setRowData).catch(console.error); // Pobierz ponownie dane po aktualizacji
      } catch (error) {
        console.error("Error updating status:", error);
        toast.error("Wystąpił błąd podczas aktualizacji statusu.");
      }
    }
  };

  const handleZaakceptujWniosek = async () => {
    try {
      // Wysłanie żądania do API w celu zmiany statusu na "Do wysłania" oraz weryfikacja na 1
      await updateField(id, {
        status: "Do wysłania",
        weryfikacja: 1,
      });

      const timestamp = moment()
        .tz("Europe/Warsaw")
        .format("YYYY-MM-DDTHH:mm:ss");

      await saveHistory({
        id: id,
        username: username, // zakładam, że masz zmienną username w zasięgu
        fieldName: "status",
        oldValue: rowData.status, // poprzednia wartość statusu
        newValue: "Do wysłania",
        timestamp: timestamp, // aktualny czas
      });

      toast.success("Status został zmieniony na Do wysłania");

      // Pobranie zaktualizowanych danych wniosku
      const updatedRowData = await fetchRowData(id);
      setRowData(updatedRowData); // Ustawienie nowych danych w stanie komponentu
    } catch (error) {
      console.error("Error changing status to Do wysłania:", error);
      toast.error("Wystąpił błąd podczas zmiany statusu.");
    }
  };

  const handleDoPoprawy = async () => {
    try {
      // 1. Zmiana statusu na "Do poprawy" oraz ustawienie weryfikacji na 0
      await updateField(id, {
        status: "Do poprawy",
        weryfikacja: 0,
      });

      const timestamp = moment()
        .tz("Europe/Warsaw")
        .format("YYYY-MM-DDTHH:mm:ss");

      // 2. Zapis do historii zmian
      await saveHistory({
        id: id,
        username: username, // zakładam, że masz zmienną username w zasięgu
        fieldName: "status",
        oldValue: rowData.status, // poprzednia wartość statusu
        newValue: "Do poprawy",
        timestamp: timestamp, // aktualny czas
      });

      toast.success("Status został zmieniony na Do poprawy.");
      // 3. Pobranie zaktualizowanych danych wniosku
      const updatedRowData = await fetchRowData(id);
      setRowData(updatedRowData); // Aktualizacja stanu komponentu
    } catch (error) {
      console.error("Error changing status to Do poprawy:", error);
      toast.error("Wystąpił błąd podczas zmiany statusu.");
    }
  };

  const handleDoPoprawyNaWniosekUC = async () => {
    try {
      // Wysłanie żądania do API w celu zmiany statusu na "Do poprawy" oraz weryfikacja na 0
      await updateField(id, {
        status: "Do poprawy na wniosek UC",
        weryfikacja: 1,
      });

      const timestamp = moment()
        .tz("Europe/Warsaw")
        .format("YYYY-MM-DDTHH:mm:ss");

      await saveHistory({
        id: id,
        username: username, // zakładam, że masz zmienną username w zasięgu
        fieldName: "status",
        oldValue: rowData.status, // poprzednia wartość statusu
        newValue: "Do poprawy na wniosek UC",
        timestamp: timestamp, // aktualny czas
      });

      toast.success(
        "Status został zmieniony na Do poprawy na wniosek UC. Proszę napisz komentarz, z informacją dlaczego jest to Do poprawy na wniosek UC. ",
      );

      // Pobranie zaktualizowanych danych wniosku
      const updatedRowData = await fetchRowData(id);
      setRowData(updatedRowData); // Ustawienie nowych danych w stanie komponentu
    } catch (error) {
      console.error(
        "Error changing status to Do poprawy na wniosek UC:",
        error,
      );
      toast.error("Wystąpił błąd podczas zmiany statusu.");
    }
  };

  const handleWycofany = async () => {
    try {
      // Wysłanie żądania do API w celu zmiany statusu na "Do poprawy" oraz weryfikacja na 0
      await updateField(id, {
        status: "Wycofany",
        weryfikacja: 0,
      });

      const timestamp = moment()
        .tz("Europe/Warsaw")
        .format("YYYY-MM-DDTHH:mm:ss");

      await saveHistory({
        id: id,
        username: username, // zakładam, że masz zmienną username w zasięgu
        fieldName: "status",
        oldValue: rowData.status, // poprzednia wartość statusu
        newValue: "Wycofany",
        timestamp: timestamp, // aktualny czas
      });

      toast.success("Status został zmieniony na Wycofany.");

      // Pobranie zaktualizowanych danych wniosku
      const updatedRowData = await fetchRowData(id);
      setRowData(updatedRowData); // Ustawienie nowych danych w stanie komponentu
    } catch (error) {
      console.error("Error changing status to Wycofany:", error);
      toast.error("Wystąpił błąd podczas zmiany statusu.");
    }
  };

  const fetchData = async () => {
    try {
      const options = await fetchPowodWpisaniaOptions();

      // Sortowanie alfabetyczne po pobraniu danych
      const sortedOptions = options.sort((a, b) =>
        a.name.localeCompare(b.name),
      );

      setPowodWpisaniaOptions(sortedOptions); // Ustaw posortowane opcje
    } catch (error) {
      toast.error("Błąd podczas pobierania powodów wpisania.");
    }
  };

  useEffect(() => {
    fetchUsersData();
    fetchData(); // Wywołanie funkcji
  }, []);

  useEffect(() => {
    if (id) {
      fetchRowData(id)
        .then((data) => {
          setRowData(data);
          setReminderSent(data.wyslano_przypomnienie);
          setLegalDeadlineChecked(!!Number(data.termin_ustawowy_odpowiedzi));
          setLegalDeadlineDate(
            data.data_terminu_odpowiedzi
              ? data.data_terminu_odpowiedzi.substring(0, 10)
              : "",
          );
          setPytanieZUrzedu(!!Number(data.pytanie_z_urzedu));
          setOdpowiedzNaPytanie(!!Number(data.odpowiedz_na_pytanie));
          setDeliveryNotApplicable(!!Number(data.doreczenie_nie_dotyczy));
          setDeliveryExceptionReason(
            String(data.powod_braku_doreczenia || "").trim(),
          );
          setDeliveryExceptionError("");

          checkAndUpdateStatus(data); // Sprawdź status po pobraniu danych
        })
        .catch(console.error);
      fetchComments(id).then(setCommentsData).catch(console.error);
      fetchValidLogins();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const [statusUpdated, setStatusUpdated] = useState(false);
  const [finalized, setFinalized] = useState(false);
  const [ckCollapsed, setCkCollapsed] = useState({});

  useEffect(() => {
    if (rowData?.status !== "Odebrany z urzędu") {
      setStatusUpdated(false);
    }

    if (rowData?.status !== "Zakończony") {
      setFinalized(false);
    }
  }, [rowData?.status]);

  useEffect(() => {
    const updateStatusIfDataOdebraniaSet = async () => {
      if (
        rowData?.data_odebrania !== null &&
        rowData?.status !== "Odebrany z urzędu" &&
        rowData?.status !== "Zakończony" &&
        rowData?.status !== "Wycofany" &&
        !statusUpdated
      ) {
        try {
          setStatusUpdated(true); // <<< tu, wcześniej
          await updateField(id, {
            status: "Odebrany z urzędu",
          });

          const timestamp = moment()
            .tz("Europe/Warsaw")
            .format("YYYY-MM-DDTHH:mm:ss");

          await saveHistory({
            id: id,
            username: username, // zakładam, że masz zmienną username w zasięgu
            fieldName: "status",
            oldValue: rowData.status, // poprzednia wartość statusu
            newValue: "Odebrany z urzędu",
            timestamp: timestamp, // aktualny czas
          });
          toast.success("Zmiana statusu na Odebrany z urzędu.");
          const updatedData = await fetchRowData(id);
          setRowData(updatedData);
        } catch (error) {
          console.error("Error updating status:", error);
          toast.error("Wystąpił błąd podczas automatycznej zmiany statusu.");
        }
      }
    };

    const updateStatusIfDataDoreczeniaSet = async () => {
      const isWezwanieRecord =
        String(rowData?.powod_wpisania || "").toUpperCase() === "WEZWANIE";

      if (isWezwanieRecord) {
        return;
      }

      const canFinalizeWithoutDeliveryDate =
        !isWezwanieRecord && hasDeliveryException(rowData);

      if (
        (rowData?.data_doreczenia || canFinalizeWithoutDeliveryDate) &&
        !finalized &&
        rowData?.status !== "Zakończony"
      ) {
        const currentDateTime = moment().format("YYYY-MM-DDTHH:mm:ss");
        try {
          setFinalized(true);
          await updateField(id, {
            status: "Zakończony",
            data_zakonczenia: currentDateTime,
          });

          const timestamp = moment()
            .tz("Europe/Warsaw")
            .format("YYYY-MM-DDTHH:mm:ss");

          await saveHistory({
            id: id,
            username: username, // zakładam, że masz zmienną username w zasięgu
            fieldName: "status",
            oldValue: rowData.status, // poprzednia wartość statusu
            newValue: "Zakończony",
            timestamp: timestamp, // aktualny czas
          });
          toast.success("Wniosek został automatycznie zakończony.");
          const updatedData = await fetchRowData(id);
          setRowData(updatedData);
        } catch (error) {
          console.error("Error updating status:", error);
          toast.error("Wystąpił błąd podczas automatycznego zakończenia.");
        }
      }
    };

    if (rowData?.data_odebrania) {
      updateStatusIfDataOdebraniaSet();
    }
    if (rowData?.data_doreczenia) {
      updateStatusIfDataDoreczeniaSet();
    } else if (hasDeliveryException(rowData)) {
      updateStatusIfDataDoreczeniaSet();
    }
  }, [
    rowData?.data_odebrania,
    rowData?.data_doreczenia,
    rowData?.doreczenie_nie_dotyczy,
    rowData?.powod_braku_doreczenia,
    rowData?.status,
    id,
    statusUpdated,
    finalized,
    username,
  ]);

  // Definicja funkcji handleFieldSelect
  const handleFieldSelect = (field) => {
    const isWezwanie =
      String(rowData?.powod_wpisania || "").toUpperCase() === "WEZWANIE";

    if (field === "status" && isWezwanie && role !== "admin") {
      toast.error("Tylko administrator może edytować status dla wezwania.");
      return;
    }

    if (
      isWezwanie &&
      !wezwanieEditableFields.includes(field) &&
      !(field === "status" && role === "admin")
    ) {
      return;
    }

    setEditField(field);
    setEditedValue(rowData[field] || "");
    setIsEditing(true);
  };

  // Function to fetch valid logins from database
  const fetchValidLogins = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/users`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });
      // Ekstrahuj same loginy (username) z tabeli users do tablicy i posortuj alfabetycznie
      const logins = response.data
        .map((user) => user.username)
        .sort((a, b) => a.localeCompare(b));
      setValidLoginsList(logins);
    } catch (error) {
      console.error("Error fetching valid logins:", error);
    }
  };

  // Function to validate additional recipients format
  const validateRecipientsFormat = (logins) => {
    if (!logins || logins.length === 0) {
      setRecipientsError("");
      return true;
    }

    // Sprawdzenie czy są puste loginy
    for (let login of logins) {
      if (!login || login.trim() === "") {
        setRecipientsError(
          "Wykryto pusty login. Usuń puste wartości (sprawdź czy nie ma spacji po przecinku).",
        );
        return false;
      }
    }

    // Sprawdzenie czy loginy zawierają spacje (poza trim)
    for (let login of logins) {
      const trimmedLogin = login.trim();
      if (trimmedLogin.includes(" ")) {
        setRecipientsError(
          `Login "${trimmedLogin}" zawiera spacje. Loginy nie mogą zawierać spacji.`,
        );
        return false;
      }
    }

    // Sprawdzenie czy loginy istnieją w bazie danych
    if (validLoginsList.length > 0) {
      const invalidLogins = [];
      for (let login of logins) {
        const normalizedLogin = login.trim();
        if (!validLoginsList.includes(normalizedLogin)) {
          invalidLogins.push(login.trim());
        }
      }

      if (invalidLogins.length > 0) {
        if (invalidLogins.length === 1) {
          setRecipientsError(
            `Login "${invalidLogins[0]}" nie istnieje w bazie danych.`,
          );
        } else {
          setRecipientsError(
            `Loginy nie istnieją w bazie danych: ${invalidLogins.join(", ")}`,
          );
        }
        return false;
      }
    }

    setRecipientsError("");
    return true;
  };

  // Handle input change - automatyczne tworzenie chipów po przecinku
  const handleInputChange = (event, newInputValue, reason) => {
    if (newInputValue.includes(",")) {
      // Jeśli użytkownik wpisał przecinek
      const parts = newInputValue.split(",");
      const loginsToAdd = parts
        .map((p) => p.trim())
        .filter((p) => p.length > 0);

      if (loginsToAdd.length > 0) {
        const newRecipients = [...additionalRecipients, ...loginsToAdd];
        setAdditionalRecipients(newRecipients);
        validateRecipientsFormat(newRecipients);
      }

      // Po przecinku wyczyść pole input i przywróć focus
      setInputValue("");

      // Przywróć focus do pola input po małym opóźnieniu
      setTimeout(() => {
        if (autocompleteRef.current) {
          const input = autocompleteRef.current.querySelector("input");
          if (input) {
            input.focus();
          }
        }
      }, 0);

      return;
    }

    if (reason === "reset") {
      // Gdy autocomplete resetuje wartość (np. po wybraniu z listy lub Enter)
      setInputValue("");
      return;
    }

    // Normalne wpisywanie
    setInputValue(newInputValue);
  };

  // Handle additional recipients change with validation
  const handleAdditionalRecipientsChange = (event, newValue, reason) => {
    // Zawsze przetwarzaj wartości - szukaj przecinków i rozdziel je
    const processedValues = [];

    newValue.forEach((value) => {
      if (typeof value === "string" && value.includes(",")) {
        // Rozdziel po przecinkach
        const splitValues = value.split(",").map((v) => v.trim());
        // Dodaj wszystkie wartości, nawet puste - walidacja je wykryje
        processedValues.push(...splitValues);
      } else if (typeof value === "string") {
        processedValues.push(value.trim());
      } else {
        processedValues.push(value);
      }
    });

    setAdditionalRecipients(processedValues);
    validateRecipientsFormat(processedValues);

    // Po dodaniu chipów wyczyść input
    if (reason === "createOption" || reason === "selectOption") {
      setInputValue("");
    }
  };

  // Filtrowanie opcji - wyszukiwanie od początku tekstu
  const filterOptions = createFilterOptions({
    matchFrom: "start",
    stringify: (option) => option,
  });

  const handleAddComment = async () => {
    if (!commentValue?.trim()) return;

    const timestamp = moment()
      .tz("Europe/Warsaw")
      .format("YYYY-MM-DDTHH:mm:ss");

    // Walidacja formatu dodatkowych odbiorców
    if (
      additionalRecipients.length > 0 &&
      !validateRecipientsFormat(additionalRecipients)
    ) {
      toast.error("Popraw format loginów dodatkowych odbiorców.");
      return;
    }

    // Przetwarzanie dodatkowych odbiorców
    const extraRecipients = additionalRecipients.filter(
      (login) => login.length > 0,
    );

    // Logika odbiorców powiadomienia
    let allRecipients;
    if (username === rowData.podpisany) {
      // Przypadek 1: Osoba dodająca komentarz = osoba podpisana
      if (extraRecipients.length === 0) {
        // Brak dodatkowych odbiorców → brak powiadomienia
        allRecipients = [];
      } else {
        // Są dodatkowi odbiorcy → wysyłamy do nich (łącznie z username jeśli jest na liście)
        allRecipients = extraRecipients;
      }
    } else {
      // Przypadek 2: Osoba dodająca komentarz ≠ osoba podpisana
      if (extraRecipients.length === 0) {
        // Brak dodatkowych odbiorców → tylko podpisany dostaje powiadomienie
        allRecipients = [rowData.podpisany];
      } else {
        // Są dodatkowi odbiorcy → podpisany + dodatkowi odbiorcy
        allRecipients = [rowData.podpisany, ...extraRecipients];
      }
    }
    // Usuń duplikaty z listy odbiorców
    allRecipients = [...new Set(allRecipients)];

    // Oblicz automatycznych odbiorców (osoby które dostaną powiadomienie bez dodawania dodatkowych)
    const automaticRecipients =
      username === rowData.podpisany ? [] : [rowData.podpisany];

    // Tworzenie treści komentarza z informacją o odbiorcach powiadomienia
    let finalComment = commentValue;

    // Połącz wszystkich odbiorców (automatycznych i dodatkowych) i usuń duplikaty
    const allRecipientsForDisplay = [
      ...new Set([...automaticRecipients, ...extraRecipients]),
    ];

    // Dodaj informację o wszystkich odbiorcach powiadomienia
    if (allRecipientsForDisplay.length > 0) {
      finalComment += `\n\n---\nOdbiorcy powiadomienia: ${allRecipientsForDisplay.join(", ")}`;
    }

    const newComment = {
      id,
      username,
      comment: finalComment,
      recipients: allRecipients,
      uniqueNumber: rowData.unique_number,
      timestamp: timestamp,
      status: rowData.status,
    };

    const commentData = {
      username,
      comment: finalComment,
      timestamp: timestamp,
    };

    try {
      // Sprawdź czy są odbiorcy do wysłania powiadomień
      const shouldSendNotification = allRecipients.length > 0;

      let result;
      if (shouldSendNotification) {
        // Wyślij komentarz z powiadomieniem - atomowa operacja (transakcja w backend)
        result = await addCommentWithNotification(newComment);
      } else {
        // Dodaj tylko komentarz bez powiadomienia
        result = await addComment(id, commentData);
        result = { success: true }; // Standardowa funkcja addComment zwraca inne dane
      }

      // Sprawdź czy operacja się powiodła
      if (result?.success) {
        // Tylko jeśli backend potwierdził sukces, dodaj komentarz lokalnie
        try {
          setCommentsData(await fetchComments(id));
        } catch (fetchError) {
          console.error("Error refreshing comments:", fetchError);
          setCommentsData([
            ...commentsData,
            {
              comment: finalComment,
              username,
              created_at: moment().format("YYYY-MM-DDTHH:mm:ss"),
            },
          ]);
        }
        setCommentValue("");
        setAdditionalRecipients([]);
        setInputValue("");

        if (shouldSendNotification) {
          toast.success("Komentarz został dodany i wysłano powiadomienie!");
        } else {
          toast.success("Komentarz został dodany!");
        }
      } else {
        // Backend zwrócił success: false
        const errorMessage =
          result?.message || "Nie udało się dodać komentarza.";
        console.error("❌ Backend zwrócił błąd:", errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("❌ Błąd przy dodawaniu komentarza:", error);

      // Pokaż szczegółowy komunikat błędu z backendu
      const backendMessage = error.response?.data?.message;

      if (error.response?.status === 400) {
        toast.error(backendMessage || "Nieprawidłowe dane wejściowe.");
      } else if (error.response?.status === 500) {
        // Błąd 500 może być: brak połączenia z bazą, błąd transakcji, błąd wysyłania emaila
        toast.error(
          backendMessage ||
            "Błąd serwera – komentarz nie został zapisany. Spróbuj ponownie.",
        );
      } else if (error.request) {
        // Brak odpowiedzi z serwera
        toast.error(
          "Brak odpowiedzi z serwera – sprawdź połączenie z internetem i spróbuj ponownie.",
        );
      } else {
        // Inny nieoczekiwany błąd
        toast.error(
          backendMessage ||
            "Nieoczekiwany błąd – komentarz nie został zapisany. Spróbuj ponownie.",
        );
      }

      // Log szczegółowy dla debugowania
      if (backendMessage) {
        console.error("📧 Szczegóły błędu z backendu:", backendMessage);
      }
    }
  };

  const isMailNotificationComment = (commentText = "") =>
    String(commentText).startsWith(mailNotificationPrefix);

  const getDisplayedCommentText = (commentText = "") => {
    if (!isMailNotificationComment(commentText)) {
      return commentText;
    }

    return String(commentText).replace(mailNotificationPrefix, "").trim();
  };

  // Kolor awatara zależny od loginu autora (ten sam autor = ten sam kolor).
  const COMMENT_AVATAR_COLORS = [
    "#1976d2",
    "#388e3c",
    "#7b1fa2",
    "#f57c00",
    "#0097a7",
    "#c2185b",
    "#5d4037",
  ];
  const getCommentAvatarColor = (author = "") => {
    const hash = [...String(author)].reduce(
      (acc, ch) => acc + ch.charCodeAt(0),
      0,
    );
    return COMMENT_AVATAR_COLORS[hash % COMMENT_AVATAR_COLORS.length];
  };

  const renderCommentItem = (comment, index) => {
    const key = comment.id ?? index;
    const createdAt = comment.created_at
      ? moment(comment.created_at).format("HH:mm, DD-MM-YYYY")
      : "";

    if (!isMailNotificationComment(comment.comment)) {
      const author = String(comment.username ?? "");
      return (
        <div key={key} className={classes.ccItem}>
          <div className={classes.ccHeader}>
            <span
              className={classes.ccAvatar}
              style={{ backgroundColor: getCommentAvatarColor(author) }}
              aria-hidden="true"
            >
              {(author.charAt(0) || "?").toUpperCase()}
            </span>
            <span className={classes.ccAuthor}>{author || "—"}</span>
            <span className={classes.ccDate}>{createdAt}</span>
          </div>
          <div className={classes.ccText}>
            {getDisplayedCommentText(comment.comment)}
          </div>
        </div>
      );
    }

    return (
      <div
        key={key}
        className={`${classes.mailCard} ${classes.mailCardIncoming}`}
      >
        <div className={classes.mailCardHeader}>
          <span className={classes.mailBadge}>
            <span aria-hidden="true">↙</span>
            Mail przychodzący
          </span>
          <span className={classes.ccDate}>{createdAt}</span>
        </div>
        <div className={classes.ccText}>
          {getDisplayedCommentText(comment.comment)}
        </div>
        <div className={classes.mailCardFooter}>
          Odnotował(a): <strong>{comment.username || "—"}</strong>
        </div>
      </div>
    );
  };

  const handleRecordMailNotification = async () => {
    const confirmed = window.confirm(
      "Pamiętaj o zapisie maila na dysku w celu poprawnej archwizacji. Czy chcesz kontynuować?",
    );

    if (!confirmed) {
      return;
    }

    try {
      const timestamp = moment()
        .tz("Europe/Warsaw")
        .format("YYYY-MM-DDTHH:mm:ss");
      const recipientUsername = mapPodpisanyToUsername(rowData?.podpisany);
      const currentUsername = username;

      const recipients =
        recipientUsername && recipientUsername !== currentUsername
          ? [recipientUsername]
          : [];

      const notificationComment = `${mailNotificationPrefix}\nOtrzymano wiadomość mailową w sprawie.\nData i godzina: ${moment().tz("Europe/Warsaw").format("HH:mm, DD-MM-YYYY")}\nKliknięte przez: ${currentUsername}`;

      if (recipients.length > 0) {
        await addCommentWithNotification({
          id,
          comment: notificationComment,
          username: currentUsername,
          recipients,
          timestamp,
          uniqueNumber: rowData.unique_number,
          status: rowData.status,
          powod_wpisania: rowData.powod_wpisania,
          nr_MRN: rowData.nr_MRN,
          nadawca_odbiorca: rowData.nadawca_odbiorca,
          odbiorca: rowData.odbiorca,
          podpisany: rowData.podpisany,
        });

        toast.success(
          "Odnotowano otrzymanie wiadomości mailowej i wysłano e-mail.",
        );
      } else {
        await addComment(id, {
          username: currentUsername,
          comment: notificationComment,
          timestamp,
        });

        toast.info(
          "Odnotowano otrzymanie wiadomości mailowej. E-mail nie został wysłany (brak odbiorcy lub próba wysyłki do siebie).",
        );
      }

      const updatedComments = await fetchComments(id);
      setCommentsData(updatedComments);
    } catch (error) {
      console.error("Error recording mail notification:", error);
      toast.error(
        error.response?.data?.message ||
          "Wystąpił błąd podczas odnotowywania wiadomości mailowej.",
      );
    }
  };

  const handleEditSave = async () => {
    try {
      const isWezwanie =
        String(rowData?.powod_wpisania || "").toUpperCase() === "WEZWANIE";

      if (editField === "status" && isWezwanie && role !== "admin") {
        toast.error("Tylko administrator może edytować status dla wezwania.");
        return;
      }

      if (
        editField === "nr_MRN" &&
        editedValue.trim() !== "" &&
        !mrnRegex.test(editedValue.trim())
      ) {
        toast.error(mrnValidationMessage);
        return;
      }

      // Specjalna obsługa dla pola status
      if (editField === "status") {
        await handleStatusChange(editedValue);
        setIsEditing(false);
        return;
      }

      if (
        editField === "data_wyslania" &&
        (!rowData.platnik || rowData.platnik === "")
      ) {
        toast.error("Musisz najpierw uzupełnić pole Płatnik!");
        return;
      }

      if (
        editField === "data_wyslania" &&
        (!rowData.sciezkaDoPliku || rowData.sciezkaDoPliku === "")
      ) {
        toast.error("Musisz najpierw uzupełnić pole Ścieżka!");
        return;
      }

      if (
        editField === "data_wyslania" &&
        !usersWithoutVerification.includes(username) &&
        (!rowData.weryfikacja || rowData.weryfikacja === "0")
      ) {
        toast.error(
          "Użytkownik plomza lub aszkutnik muszą zaakceptować wniosek!",
        );
        return;
      }

      if (
        editField === "data_odebrania" &&
        (!rowData.data_wyslania || rowData.data_wyslania === "")
      ) {
        toast.error("Najpierw musi zostać uzupełniona data wysłania.");
        return;
      }

      if (
        editField === "data_doreczenia" &&
        rowData?.doreczenie_nie_dotyczy
      ) {
        toast.error(
          "Najpierw odznacz wyjątek doręczenia, aby uzupełnić datę doręczenia.",
        );
        return;
      }

      if (
        editField === "data_doreczenia" &&
        (!rowData.data_odebrania || rowData.data_odebrania === "")
      ) {
        toast.error(
          "Musisz najpierw uzupełnić pole Data odebrania decyzji z UC!",
        );
        return;
      }

      const oldValue = rowData[editField];
      const newValue = [
        "data_wpisania",
        "data_SAD",
        "data_wyslania",
        "data_odebrania",
        "data_odpowiedzi_na_pytanie",
      ].includes(editField)
        ? moment(editedValue).format("YYYY-MM-DD")
        : editedValue;

      await updateField(id, { [editField]: newValue });

      const timestamp = moment()
        .tz("Europe/Warsaw")
        .format("YYYY-MM-DDTHH:mm:ss");

      await logHistory(id, {
        field: editField,
        oldValue: oldValue,
        newValue: newValue,
        username: username,
        timestamp: timestamp,
      });

      // Jeśli edytowane pole to data_wyslania i user może pominąć weryfikację
      if (
        editField === "data_wyslania" &&
        usersWithoutVerification.includes(username) &&
        rowData.sciezkaDoPliku &&
        rowData.platnik &&
        (rowData.powod_wpisania === "CC599C" ||
          rowData.powod_wpisania === "cc599c")
      ) {
        await updateField(id, { status: "Wysłany" });

        const timestamp = moment()
          .tz("Europe/Warsaw")
          .format("YYYY-MM-DDTHH:mm:ss");

        await saveHistory({
          id: id,
          username,
          fieldName: "status",
          oldValue: rowData.status,
          newValue: "Wysłany",
          timestamp: timestamp,
        });

        toast.success("Status automatycznie zmieniony na Wysłany");
      } else {
        toast.success("Poprawnie zedytowano dane!");
      }

      setIsEditing(false);

      const updatedRowData = await fetchRowData(id);
      setRowData(updatedRowData);
      checkAndUpdateStatus(updatedRowData);
    } catch (error) {
      console.error("Error updating data:", error);
      toast.error(getApiErrorMessage(error, "Error updating data."));
    }
  };

  const handleEditCancel = () => {
    setIsEditing(false);
  };

  if (!rowData || !id) {
    return (
      <div className={classes.loaderContainer}>
        <CircularProgress color="secondary" />
        <Typography variant="body1">Loading...</Typography>
      </div>
    );
  }

  const isWezwanie =
    String(rowData?.powod_wpisania || "").toUpperCase() === "WEZWANIE";

  const categoriesToRender = isWezwanie
    ? {
        "Szczegóły wezwania": [...wezwanieEditableFields, "status"],
      }
    : categories;

  const canEditField = (field) => {
    if (field === "podpisany") {
      return (
        role === "admin" ||
        username === "adobrowolska" ||
        username === "aszkutnik"
      );
    }

    if (field === "status") {
      return role === "admin";
    }

    if (isWezwanie) {
      return wezwanieEditableFields.includes(field);
    }

    return true;
  };

  const automaticRecipientsWithoutCurrentUser =
    username === rowData?.podpisany ? [] : [rowData?.podpisany].filter(Boolean);

  // ---- Sekcja znaczników: chipy zamiast listy checkboxów ----
  const ckChip = (name, checked, label, onToggle) => (
    <label
      key={name}
      className={[classes.ckChip, checked && classes.ckChipChecked]
        .filter(Boolean)
        .join(" ")}
    >
      <input
        type="checkbox"
        className={classes.ckInput}
        name={name}
        checked={checked}
        onChange={(event) => onToggle(event.target.checked)}
      />
      {checked && <span aria-hidden="true">✓</span>}
      {label}
    </label>
  );

  const ckCount = (chips) => chips.filter(([, checked]) => checked).length;

  const ckCard = (key, title, count, children) => {
    const collapsed = Boolean(ckCollapsed[key]);
    return (
      <div className={classes.ckCard} key={key}>
        <button
          type="button"
          className={classes.ckCardHeader}
          onClick={() =>
            setCkCollapsed((prev) => ({ ...prev, [key]: !prev[key] }))
          }
          aria-expanded={!collapsed}
        >
          <span className={classes.ckCardTitle}>{title}</span>
          {count > 0 && <span className={classes.ckCount}>{count}</span>}
          <span className={classes.ckArrow}>{collapsed ? "▸" : "▾"}</span>
        </button>
        {!collapsed && <div className={classes.ckCardBody}>{children}</div>}
      </div>
    );
  };

  const handleProbaOAacceptacje = async () => {
    try {
      if (!rowData.sciezkaDoPliku || rowData.sciezkaDoPliku === "") {
        toast.error("Musisz najpierw uzupełnić pole Ścieżka!");
        return;
      }

      await updateField(id, { status: "Do weryfikacji" });

      const timestamp = moment()
        .tz("Europe/Warsaw")
        .format("YYYY-MM-DDTHH:mm:ss");

      await saveHistory({
        id: id,
        username,
        fieldName: "status",
        oldValue: rowData.status,
        newValue: "Do weryfikacji",
        timestamp: timestamp,
      });

      toast.success("Status został zmieniony na Do weryfikacji");

      const updatedRowData = await fetchRowData(id);
      setRowData(updatedRowData);
    } catch (error) {
      console.error("Error changing status:", error);
      toast.error("Wystąpił błąd podczas zmiany statusu.");
    }
  };

  const handlePytanieZUrzeduChange = (checked) => {
    setPytanieZUrzedu(checked);
    if (checked) {
      // Odznaczamy "Odpowiedź na pytanie" jeśli była zaznaczona
      if (odpowiedzNaPytanie) {
        setOdpowiedzNaPytanie(false);
      }
    }
  };

  const handleOdpowiedzNaPytanieChange = (checked) => {
    setOdpowiedzNaPytanie(checked);
    if (checked) {
      // Jeśli zaznaczamy "Odpowiedź na pytanie", odznaczamy powiązane pola
      setPytanieZUrzedu(false);
      setLegalDeadlineChecked(false);
      setLegalDeadlineDate("");
    }
  };

  const handleDeliveryNotApplicableChange = (checked) => {
    if (checked && rowData?.data_doreczenia) {
      toast.error(
        "Najpierw usuń datę doręczenia, jeśli chcesz oznaczyć, że doręczenie nie dotyczy.",
      );
      return;
    }

    setDeliveryNotApplicable(checked);

    if (!checked) {
      setDeliveryExceptionReason("");
      setDeliveryExceptionError("");
    }
  };

  const handleTerminUstawowyChange = (checked) => {
    setLegalDeadlineChecked(checked);
    if (checked) {
      // Odznaczamy "Odpowiedź na pytanie" jeśli była zaznaczona
      if (odpowiedzNaPytanie) {
        setOdpowiedzNaPytanie(false);
      }
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const canFinishCurrentRecord =
        String(rowData?.powod_wpisania || "").toUpperCase() === "WEZWANIE" ||
        rowData?.data_doreczenia ||
        hasDeliveryException(rowData);

      if (newStatus === "Zakończony" && !canFinishCurrentRecord) {
        toast.error(
          "Aby zakończyć wniosek, uzupełnij datę doręczenia albo zaznacz wyjątek i podaj powód.",
        );
        return;
      }

      const oldStatus = rowData.status;
      const currentTimestamp = moment()
        .tz("Europe/Warsaw")
        .format("YYYY-MM-DDTHH:mm:ss");

      const statusPayload = { status: newStatus };
      if (newStatus === "Zakończony") {
        statusPayload.data_zakonczenia = currentTimestamp;
      }
      if (newStatus === "Odebrany z urzędu") {
        statusPayload.data_zakonczenia = null;
      }

      await updateField(id, statusPayload);

      // Logowanie historii zmiany
      await logHistory(id, {
        field: "status",
        oldValue: oldStatus,
        newValue: newStatus,
        username: username,
        timestamp: currentTimestamp,
      });

      setRowData((prev) => ({
        ...prev,
        status: newStatus,
        data_zakonczenia:
          newStatus === "Zakończony"
            ? currentTimestamp
            : newStatus === "Odebrany z urzędu"
              ? null
              : prev.data_zakonczenia,
      }));
      toast.success(`Status został zmieniony na: ${newStatus}`);
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(
        getApiErrorMessage(
          error,
          "Wystąpił błąd podczas aktualizacji statusu.",
        ),
      );
    }
  };

  const handleSaveCheckbox = async () => {
    if (legalDeadlineChecked && legalDeadlineDate === "") {
      setDateError(true);
      toast.error("Uzupełnij datę terminu ustawowego na odpowiedź");
      return;
    }

    setDateError(false);

    const isWezwanieRecord =
      String(rowData?.powod_wpisania || "").toUpperCase() === "WEZWANIE";
    const trimmedDeliveryExceptionReason = deliveryExceptionReason.trim();

    if (!isWezwanieRecord && deliveryNotApplicable && rowData?.data_doreczenia) {
      setDeliveryExceptionError(
        "Usuń datę doręczenia przed zapisaniem wyjątku.",
      );
      toast.error("Usuń datę doręczenia przed zapisaniem wyjątku.");
      return;
    }

    if (!isWezwanieRecord && deliveryNotApplicable && !rowData?.data_odebrania) {
      setDeliveryExceptionError(
        "Najpierw musi zostać uzupełniona data odebrania decyzji z UC.",
      );
      toast.error(
        "Najpierw musi zostać uzupełniona data odebrania decyzji z UC.",
      );
      return;
    }

    if (!isWezwanieRecord && deliveryNotApplicable && !trimmedDeliveryExceptionReason) {
      setDeliveryExceptionError("Uzupełnij powód braku daty doręczenia.");
      toast.error("Uzupełnij powód braku daty doręczenia.");
      return;
    }

    setDeliveryExceptionError("");

    try {
      const currentTimestamp = moment()
        .tz("Europe/Warsaw")
        .format("YYYY-MM-DDTHH:mm:ss");

      const pytanieZUrzeduTimestamp = pytanieZUrzedu ? currentTimestamp : null;
      const odpowiedzNaPytanieTimestamp = odpowiedzNaPytanie
        ? currentTimestamp
        : null;

      // Zapisz dane znaczników
      await saveReminderStatus(
        rowData.id,
        reminderSent,
        legalDeadlineChecked,
        legalDeadlineChecked ? legalDeadlineDate : null,
        pytanieZUrzedu,
        pytanieZUrzeduTimestamp,
        odpowiedzNaPytanie,
        odpowiedzNaPytanieTimestamp,
        !isWezwanieRecord ? deliveryNotApplicable : false,
        !isWezwanieRecord && deliveryNotApplicable
          ? trimmedDeliveryExceptionReason
          : null,
      );

      // Sprawdź zmiany w "Termin ustawowy wezwania" i zapisz do historii
      const previousLegalDeadlineChecked = !!Number(
        rowData.termin_ustawowy_odpowiedzi,
      );
      if (legalDeadlineChecked !== previousLegalDeadlineChecked) {
        await saveHistory({
          id: id,
          username: username,
          fieldName: "termin_ustawowy_odpowiedzi",
          oldValue: previousLegalDeadlineChecked ? "Tak" : "Nie",
          newValue: legalDeadlineChecked ? "Tak" : "Nie",
          timestamp: currentTimestamp,
        });
      }

      // Sprawdź zmiany w "Pytanie z urzędu" i zapisz do historii
      const previousPytanieZUrzedu = !!Number(rowData.pytanie_z_urzedu);
      if (pytanieZUrzedu !== previousPytanieZUrzedu) {
        await saveHistory({
          id: id,
          username: username,
          fieldName: "pytanie_z_urzedu",
          oldValue: previousPytanieZUrzedu ? "Tak" : "Nie",
          newValue: pytanieZUrzedu ? "Tak" : "Nie",
          timestamp: currentTimestamp,
        });
      }

      // Sprawdź zmiany w "Odpowiedź na pytanie" i zapisz do historii
      const previousOdpowiedzNaPytanie = !!Number(rowData.odpowiedz_na_pytanie);
      if (odpowiedzNaPytanie !== previousOdpowiedzNaPytanie) {
        await saveHistory({
          id: id,
          username: username,
          fieldName: "odpowiedz_na_pytanie",
          oldValue: previousOdpowiedzNaPytanie ? "Tak" : "Nie",
          newValue: odpowiedzNaPytanie ? "Tak" : "Nie",
          timestamp: currentTimestamp,
        });

        // Jeśli zaznaczono "Odpowiedź na pytanie", zapisz historię wyczyszczenia powiązanych pól
        if (odpowiedzNaPytanie) {
          // Historia dla pytanie_z_urzedu
          if (!!Number(rowData.pytanie_z_urzedu)) {
            await saveHistory({
              id: id,
              username: username,
              fieldName: "pytanie_z_urzedu",
              oldValue: "Tak",
              newValue: "Nie (wyczyszczone przez odpowiedź na pytanie)",
              timestamp: currentTimestamp,
            });
          }

          // Historia dla termin_ustawowy_odpowiedzi
          if (!!Number(rowData.termin_ustawowy_odpowiedzi)) {
            await saveHistory({
              id: id,
              username: username,
              fieldName: "termin_ustawowy_odpowiedzi",
              oldValue: "Tak",
              newValue: "Nie (wyczyszczone przez odpowiedź na pytanie)",
              timestamp: currentTimestamp,
            });
          }

          // Historia dla data_terminu_odpowiedzi
          if (rowData.data_terminu_odpowiedzi) {
            await saveHistory({
              id: id,
              username: username,
              fieldName: "data_terminu_odpowiedzi",
              oldValue: rowData.data_terminu_odpowiedzi,
              newValue: "Wyczyszczone (przez odpowiedź na pytanie)",
              timestamp: currentTimestamp,
            });
          }
        }
      }

      const previousDeliveryNotApplicable = !!Number(
        rowData.doreczenie_nie_dotyczy,
      );
      if (
        !isWezwanieRecord &&
        deliveryNotApplicable !== previousDeliveryNotApplicable
      ) {
        await saveHistory({
          id: id,
          username: username,
          fieldName: "doreczenie_nie_dotyczy",
          oldValue: previousDeliveryNotApplicable ? "Tak" : "Nie",
          newValue: deliveryNotApplicable ? "Tak" : "Nie",
          timestamp: currentTimestamp,
        });
      }

      const previousDeliveryExceptionReason = String(
        rowData.powod_braku_doreczenia || "",
      ).trim();
      const nextDeliveryExceptionReason =
        !isWezwanieRecord && deliveryNotApplicable
          ? trimmedDeliveryExceptionReason
          : "";

      if (
        !isWezwanieRecord &&
        previousDeliveryExceptionReason !== nextDeliveryExceptionReason
      ) {
        await saveHistory({
          id: id,
          username: username,
          fieldName: "powod_braku_doreczenia",
          oldValue: previousDeliveryExceptionReason || "Brak",
          newValue: nextDeliveryExceptionReason || "Brak",
          timestamp: currentTimestamp,
        });
      }

      // Dla WEZWANIE: odpowiedź na pytanie kończy sprawę,
      // a nowe pytanie z urzędu przywraca status odebrania.
      if (
        isWezwanieRecord &&
        odpowiedzNaPytanie &&
        rowData.status !== "Zakończony"
      ) {
        await updateField(id, {
          status: "Zakończony",
          data_zakonczenia: currentTimestamp,
        });
        await saveHistory({
          id: id,
          username: username,
          fieldName: "status",
          oldValue: rowData.status,
          newValue: "Zakończony",
          timestamp: currentTimestamp,
        });
      } else if (
        isWezwanieRecord &&
        pytanieZUrzedu &&
        rowData.status === "Zakończony"
      ) {
        await updateField(id, {
          status: "Odebrany z urzędu",
          data_zakonczenia: null,
        });
        await saveHistory({
          id: id,
          username: username,
          fieldName: "status",
          oldValue: rowData.status,
          newValue: "Odebrany z urzędu",
          timestamp: currentTimestamp,
        });
      }

      const updatedData = await fetchRowData(id);
      setRowData(updatedData);

      toast.success("Zapisano dane znaczników");
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, "Błąd zapisu danych znaczników"),
      );
    }
  };

  const navigateToKwDetail = (kwId) => {
    navigate(`/kontrolaWewnetrzna/szczegoly/${kwId}`, {
      state: {
        returnPath: `/rejestrWnioskow/${id}`,
        returnLabel: "Powrót do RW",
        source: "rejestrWnioskow",
        wniosekId: id,
      },
    });
  };

  const handleCloseKwSelectionDialog = () => {
    setIsKwSelectionDialogOpen(false);
    setKwMatches([]);
  };

  const handleOpenKwByMrn = async () => {
    const normalizedMrn = normalizeMrnForComparison(rowData?.nr_MRN);

    if (!normalizedMrn) {
      toast.warning("Brak MRN w tym wniosku.");
      return;
    }

    setIsKwLookupLoading(true);

    try {
      const response = await axios.get(`${BASE_URL}/kw-dane`, {
        params: {
          mrn: normalizedMrn,
          page: 1,
          limit: 100,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      const items = Array.isArray(response.data?.items)
        ? response.data.items
        : [];
      const exactMatches = items.filter(
        (item) => normalizeMrnForComparison(item.mrn) === normalizedMrn,
      );

      if (!exactMatches.length) {
        toast.info("Nie znaleziono wpisu KW dla tego MRN.");
        return;
      }

      if (exactMatches.length === 1) {
        navigateToKwDetail(exactMatches[0].id);
        return;
      }

      setKwMatches(exactMatches);
      setIsKwSelectionDialogOpen(true);
    } catch (error) {
      console.error("Błąd podczas wyszukiwania wpisów KW po MRN:", error);
      toast.error(
        error.response?.data?.message ||
          "Nie udało się pobrać wpisów KW dla wskazanego MRN.",
      );
    } finally {
      setIsKwLookupLoading(false);
    }
  };

  return (
    <div className={classes.root}>
      <main className={classes.content}>
        <div className={pageClasses.pageHeader}>
          <div>
            <h1 className={pageClasses.title}>
              {isWezwanie ? "Szczegóły wezwania" : "Szczegóły wniosku"}: {rowData.unique_number}
            </h1>
            <div className={pageClasses.subtitle}>Rejestr wniosków</div>
          </div>
        </div>

        {isEditing && (
          <div className={classes.dialog}>
            <Typography variant="h6" className={classes.dialogTitle}>
              Edytuj: {fieldLabels[editField] || editField}
            </Typography>
            {[
              "data_wpisania",
              "data_SAD",
              "data_wyslania",
              "data_odebrania",
              "data_odpowiedzi_na_pytanie",
              "data_doreczenia",
            ].includes(editField) ? (
              <TextField
                label={fieldLabels[editField] || editField}
                type="date"
                variant="outlined"
                fullWidth
                value={moment(editedValue).format("YYYY-MM-DD")}
                onChange={(event) => setEditedValue(event.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            ) : ["platnik"].includes(editField) ? (
              <FormControl variant="outlined" fullWidth>
                <InputLabel>{fieldLabels[editField]}</InputLabel>
                <Select
                  value={editedValue}
                  onChange={(event) => setEditedValue(event.target.value)}
                  label={fieldLabels[editField]}
                >
                  {platnikOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : ["podpisany"].includes(editField) ? (
              <FormControl variant="outlined" fullWidth>
                <InputLabel>{fieldLabels[editField]}</InputLabel>
                <Select
                  value={editedValue}
                  onChange={(event) => setEditedValue(event.target.value)}
                  label={fieldLabels[editField]}
                >
                  {usersOptions.map((option) => (
                    <MenuItem key={option.id} value={option.name}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : ["powod_wpisania"].includes(editField) ? (
              <FormControl variant="outlined" fullWidth>
                <InputLabel>{fieldLabels[editField]}</InputLabel>
                <Select
                  value={editedValue}
                  onChange={(event) => setEditedValue(event.target.value)}
                  label={fieldLabels[editField]}
                >
                  {powodWpisaniaOptions.map((option) => (
                    <MenuItem key={option.id} value={option.name}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : ["status"].includes(editField) ? (
              <FormControl variant="outlined" fullWidth>
                <InputLabel>{fieldLabels[editField]}</InputLabel>
                <Select
                  value={editedValue}
                  onChange={(event) => setEditedValue(event.target.value)}
                  label={fieldLabels[editField]}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                label={fieldLabels[editField] || editField}
                variant="outlined"
                fullWidth
                value={editedValue}
                onChange={(event) => setEditedValue(event.target.value)}
                error={
                  editField === "nr_MRN" &&
                  editedValue.trim() !== "" &&
                  !mrnRegex.test(editedValue.trim())
                }
                helperText={
                  editField === "nr_MRN" &&
                  editedValue.trim() !== "" &&
                  !mrnRegex.test(editedValue.trim())
                    ? mrnValidationMessage
                    : ""
                }
              />
            )}
            <div className={classes.buttonGroup}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleEditSave}
              >
                Zapisz
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleEditCancel}
              >
                Anuluj
              </Button>
            </div>
          </div>
        )}

        {!isEditing &&
          Object.keys(categoriesToRender).map((category) => (
            <div key={category} className={classes.categoryRow}>
              <Typography variant="h6" className={classes.categoryHeading}>
                {category}
              </Typography>
              <div className={classes.fieldBlocksRow}>
                {categoriesToRender[category].map((field) =>
                  !canEditField(field) ? null : (
                    <div
                      key={field}
                      className={
                        String(rowData[field] ?? "").trim() !== ""
                          ? `${classes.fieldBlock} ${classes.fieldBlockValid}`
                          : classes.fieldBlock
                      }
                      onClick={() => handleFieldSelect(field)}
                    >
                      <Typography className={classes.fieldLabel}>
                        {fieldLabels[field] || field}
                      </Typography>
                    </div>
                  ),
                )}
              </div>
            </div>
          ))}

        <hr className={classes.wider} />
        {!isWezwanie &&
          (rowData.status === "W toku" ||
            rowData.status === "Do poprawy" ||
            rowData.status === "Do poprawy na wniosek UC") && (
            <>
              {rowData.powod_wpisania === "CC599C" &&
              usersWithoutVerification.includes(username) ? (
                <Typography
                  variant="body1"
                  color="textSecondary"
                  style={{ marginTop: "20px", marginBottom: "20px" }}
                >
                  Wniosek po uzupełnieniu wszystkich wymaganych pól (Płatnik,
                  Ścieżka, Data wysłania) zostanie automatycznie zmieniony na
                  status Wysłany — etap weryfikacji jest pomijany.
                </Typography>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleProbaOAacceptacje}
                  style={{
                    marginTop: "20px",
                    marginBottom: "20px",
                  }}
                  endIcon={<SendIcon />}
                >
                  Wyślij prośbę o akceptację
                </Button>
              )}
              <hr className={classes.wider} />
            </>
          )}

        {rowData.status === "Do weryfikacji" &&
          (username === "plomza" ||
            username === "Maciej" ||
            username === "MaciejAdmin" ||
            username === "testowe" ||
            username === "aszkutnik") && (
            <div>
              <Button
                variant="contained"
                color="primary"
                onClick={handleZaakceptujWniosek}
                style={{
                  marginRight: "10px",
                  marginTop: "20px",
                  marginBottom: "20px",
                }}
              >
                Zaakceptuj wniosek
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleDoPoprawy}
                style={{
                  marginRight: "10px",
                  marginTop: "20px",
                  marginBottom: "20px",
                }}
              >
                Do poprawy
              </Button>
              <hr className={classes.wider} />
            </div>
          )}

        {rowData.status === "Wysłany" && (
          <div>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleDoPoprawyNaWniosekUC}
              style={{
                marginRight: "10px",
                marginTop: "20px",
                marginBottom: "20px",
              }}
            >
              Do poprawy na wniosek UC
            </Button>
            <hr className={classes.wider} />
          </div>
        )}

        {rowData.status !== "Wycofany" && rowData.status !== "Zakończony" && (
          <div>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleWycofany}
              style={{
                marginRight: "10px",
                marginTop: "20px",
                marginBottom: "20px",
              }}
            >
              {isWezwanie ? "Wycofaj wezwanie" : "Wycofaj wniosek"}
            </Button>
            <hr className={classes.wider} />
          </div>
        )}

        {(() => {
          const kwButton = (
            <Button
              variant="outlined"
              color="primary"
              onClick={handleOpenKwByMrn}
              disabled={
                isKwLookupLoading || !String(rowData.nr_MRN || "").trim()
              }
              style={{ marginTop: "8px" }}
            >
              {isKwLookupLoading
                ? "Wyszukiwanie KW..."
                : "Przejdź do KW po MRN"}
            </Button>
          );

          const deliveryNotApplicableRows =
            Number(rowData.doreczenie_nie_dotyczy) === 1 &&
            rowData.powod_braku_doreczenia
              ? [["Powód braku daty doręczenia", rowData.powod_braku_doreczenia]]
              : [];

          const leftCards = isWezwanie
            ? [
                [
                  "Dane wezwania",
                  [
                    ["Numer wezwania", rowData.unique_number],
                    ["Ścieżka", rowData.sciezkaDoPliku],
                    [
                      "Data wpisania",
                      formatDate(rowData.data_wpisania, "YYYY-MM-DD HH:mm"),
                    ],
                    ["Powód wpisania", rowData.powod_wpisania],
                    ["Utworzony przez", rowData.utworzony_przez],
                  ],
                ],
              ]
            : [
                [
                  "Dane wniosku",
                  [
                    ["Numer wniosku", rowData.unique_number],
                    ["Ścieżka", rowData.sciezkaDoPliku],
                    ["Płatnik", rowData.platnik],
                    [
                      "Data wpisania",
                      formatDate(rowData.data_wpisania, "YYYY-MM-DD HH:mm"),
                    ],
                    ["Powód wpisania", rowData.powod_wpisania],
                    ["Data SAD-u", formatDate(rowData.data_SAD)],
                    ["Utworzony przez", rowData.utworzony_przez],
                  ],
                ],
                [
                  "Procedura",
                  [
                    ["Procedura", rowData.procedura],
                    ["Kontrolujący", rowData.kontrolujacy],
                  ],
                ],
              ];

          const rightCards = isWezwanie
            ? [
                [
                  "Strony i dokumenty",
                  [
                    ["MRN", rowData.nr_MRN],
                    ["Nadawca", rowData.nadawca_odbiorca],
                    ["Adresat", rowData.odbiorca],
                    ["Os. odpowiedzialna", rowData.podpisany],
                  ],
                  true,
                ],
                [
                  "Terminy",
                  [
                    [
                      "Data odebrania decyzji z UC",
                      formatDate(rowData.data_odebrania),
                    ],
                    [
                      "Data odpowiedzi",
                      formatDate(rowData.data_odpowiedzi_na_pytanie),
                    ],
                    [
                      "Data doręczenia do Kontrahenta",
                      formatDate(rowData.data_doreczenia),
                    ],
                    [
                      "Data zakończenia",
                      formatDate(rowData.data_zakonczenia, "YYYY-MM-DD HH:mm"),
                    ],
                  ],
                ],
              ]
            : [
                [
                  "Strony i dokumenty",
                  [
                    ["MRN", rowData.nr_MRN],
                    ["Nr. akt", rowData.nr_akt],
                    ["Nadawca", rowData.nadawca_odbiorca],
                    ["Odbiorca", rowData.odbiorca],
                    ["Os. odpowiedzialna", rowData.podpisany],
                    ["Rodzaj SAD-u", rowData.rodzaj_SAD],
                  ],
                  true,
                ],
                [
                  "Terminy",
                  [
                    [
                      "Data wysłania do UC",
                      formatDate(rowData.data_wyslania),
                    ],
                    [
                      "Data odebrania decyzji z UC",
                      formatDate(rowData.data_odebrania),
                    ],
                    [
                      "Data odpowiedzi",
                      formatDate(rowData.data_odpowiedzi_na_pytanie),
                    ],
                    [
                      "Data doręczenia do Kontrahenta",
                      formatDate(rowData.data_doreczenia),
                    ],
                    [
                      "Doręczenie nie dotyczy",
                      Number(rowData.doreczenie_nie_dotyczy) ? "Tak" : "Nie",
                    ],
                    ...deliveryNotApplicableRows,
                    [
                      "Data zakończenia",
                      formatDate(rowData.data_zakonczenia, "YYYY-MM-DD HH:mm"),
                    ],
                  ],
                ],
              ];

          const renderCard = ([title, rows, withKwButton]) => (
            <div key={title} className={classes.nhCard}>
              <div className={classes.nhCardTitle}>{title}</div>
              {rows.map(([label, value]) =>
                renderNhRow(classes, label, value),
              )}
              {withKwButton && kwButton}
            </div>
          );

          return (
            <>
              {role === "admin" && (
                <div className={classes.nhStatusBar}>
                  <span className={classes.nhStatusLabel}>Status</span>
                  <span className={classes.nhStatusValue}>
                    {rowData.status || "—"}
                  </span>
                </div>
              )}
              <div className={classes.nhSummaryWrap}>
                <div className={classes.nhSummaryCol}>
                  {leftCards.map(renderCard)}
                </div>
                <div className={classes.nhSummaryCol}>
                  {rightCards.map(renderCard)}
                </div>
              </div>
            </>
          );
        })()}

        <div className={classes.ckSection}>
          <div className={classes.ckSectionHeader}>Znaczniki</div>
          <div className={classes.ckGrid}>
            {ckCard(
              "terminy",
              "Przypomnienie i termin ustawowy",
              ckCount([
                [null, reminderSent],
                [null, legalDeadlineChecked],
              ]),
              <>
                <div className={classes.ckChips}>
                  {ckChip(
                    "reminderSent",
                    reminderSent,
                    "Wysłano przypomnienie",
                    (checked) => setReminderSent(checked),
                  )}
                  {ckChip(
                    "legalDeadline",
                    legalDeadlineChecked,
                    "Termin ustawowy wezwania z urzędu",
                    handleTerminUstawowyChange,
                  )}
                </div>
                {legalDeadlineChecked && (
                  <TextField
                    label="Data terminu ustawowego"
                    type="date"
                    value={legalDeadlineDate}
                    onChange={(e) => setLegalDeadlineDate(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={dateError && legalDeadlineDate === ""}
                    helperText={
                      dateError && legalDeadlineDate === ""
                        ? "Uzupełnij datę"
                        : ""
                    }
                    margin="normal"
                  />
                )}
              </>,
            )}

            {ckCard(
              "pytania",
              "Pytania z urzędu",
              ckCount([
                [null, pytanieZUrzedu],
                [null, odpowiedzNaPytanie],
              ]),
              <>
                <div className={classes.ckSubTitle}>
                  Brak ustawowego terminu (telefon/email)
                </div>
                <div className={classes.ckChips}>
                  {ckChip(
                    "pytanieZUrzedu",
                    pytanieZUrzedu,
                    "Pytanie z urzędu",
                    handlePytanieZUrzeduChange,
                  )}
                </div>
                <div className={classes.ckSubTitle}>
                  Sprawa zostanie usunięta z bieżących zapytań z urzędu
                </div>
                <div className={classes.ckChips}>
                  {ckChip(
                    "odpowiedzNaPytanie",
                    odpowiedzNaPytanie,
                    "Potwierdź udzielenie odpowiedzi na pytanie",
                    handleOdpowiedzNaPytanieChange,
                  )}
                </div>
              </>,
            )}

            {!isWezwanie &&
              ckCard(
                "doreczenie",
                "Doręczenie do kontrahenta",
                ckCount([[null, deliveryNotApplicable]]),
                <>
                  <div className={classes.ckChips}>
                    {ckChip(
                      "deliveryNotApplicable",
                      deliveryNotApplicable,
                      "Wniosek nie będzie doręczany do kontrahenta",
                      handleDeliveryNotApplicableChange,
                    )}
                  </div>
                  {deliveryNotApplicable && (
                    <TextField
                      label="Powód braku daty doręczenia"
                      variant="outlined"
                      fullWidth
                      multiline
                      minRows={3}
                      margin="normal"
                      value={deliveryExceptionReason}
                      onChange={(event) => {
                        setDeliveryExceptionReason(event.target.value);
                        if (event.target.value.trim()) {
                          setDeliveryExceptionError("");
                        }
                      }}
                      error={!!deliveryExceptionError}
                      helperText={deliveryExceptionError}
                    />
                  )}
                </>,
              )}
          </div>

          <div className={classes.ckActionBar}>
            <Button
              className={classes.ckSaveButton}
              style={{ width: "100%" }}
              variant="contained"
              color="primary"
              onClick={handleSaveCheckbox}
            >
              Zapisz dane znaczników
            </Button>
          </div>
        </div>

        <hr className={classes.wider} />

        {!isEditing && (
          <div>
            <div className={classes.cmCard}>
            <div className={classes.cmTitle}>Dodaj komentarz</div>

            <div className={classes.cmInfo}>
              Powiadomienie mailowe zostanie automatycznie wysłane do os.
              odpowiedzialnej.
              <div style={{ marginTop: 4 }}>
                Otrzymają:{" "}
                <strong>
                  {automaticRecipientsWithoutCurrentUser.length > 0
                    ? automaticRecipientsWithoutCurrentUser.join(", ")
                    : "(brak automatycznych odbiorców)"}
                </strong>
              </div>
            </div>

            <label className={classes.cmLabel} htmlFor="wniosek-comment-text">
              Treść komentarza
            </label>
            <textarea
              id="wniosek-comment-text"
              className={classes.cmTextarea}
              value={commentValue}
              onChange={(event) => setCommentValue(event.target.value)}
              rows={5}
              placeholder="Wpisz swój komentarz..."
            />

            <div className={classes.cmLabelRow}>
              <span className={classes.cmLabel}>
                Dodatkowi odbiorcy powiadomienia (opcjonalnie)
              </span>
              <Tooltip
                title="Bez podania dodatkowych odbiorców, powiadomienie zostanie wysłane tylko do osoby odpowiedzialnej"
                arrow
                placement="right"
                componentsProps={{
                  tooltip: {
                    sx: {
                      fontSize: "14px",
                      maxWidth: "400px",
                    },
                  },
                }}
              >
                <InfoIcon
                  style={{
                    fontSize: "20px",
                    color: "#1976d2",
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
            </div>
            <div ref={autocompleteRef} style={{ width: "100%" }}>
              <Autocomplete
                key={additionalRecipients.length}
                multiple
                freeSolo
                clearOnBlur={false}
                autoHighlight
                selectOnFocus
                options={validLoginsList}
                value={additionalRecipients}
                onChange={handleAdditionalRecipientsChange}
                inputValue={inputValue}
                onInputChange={handleInputChange}
                filterOptions={filterOptions}
                ListboxProps={{
                  sx: {
                    "& .MuiAutocomplete-option[aria-selected='true']": {
                      backgroundColor: "#bbdefb !important",
                    },
                    "& .MuiAutocomplete-option.Mui-focused": {
                      backgroundColor: "#bbdefb !important",
                    },
                    "& .MuiAutocomplete-option:hover": {
                      backgroundColor: "#e3f2fd !important",
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Wpisz lub wybierz loginy (oddziel przecinkami lub Enter)"
                    error={!!recipientsError}
                    size="small"
                    style={{
                      marginTop: "5px",
                      marginBottom: recipientsError ? "5px" : "10px",
                    }}
                    helperText="Możesz wpisać ręcznie (np: plomza,adobrowolska) lub wybrać z listy."
                  />
                )}
              />
            </div>
            {recipientsError && (
              <Typography
                variant="caption"
                style={{
                  color: "#f44336",
                  marginBottom: "10px",
                  display: "block",
                  fontSize: "12px",
                }}
              >
                {recipientsError}
              </Typography>
            )}
            <div className={classes.cmFooter}>
              <Button
                className={classes.ckSaveButton}
                variant="contained"
                color="primary"
                onClick={handleAddComment}
              >
                Zapisz
              </Button>
            </div>
            </div>
            <Typography variant="h6" className={classes.komentarzeHeading}>
              Komentarze:
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              className={classes.mailNotificationButton}
              onClick={handleRecordMailNotification}
            >
              Otrzymano wiadomość mailową
            </Button>
            <div>
              {commentsData.map(renderCommentItem)}
            </div>
          </div>
        )}
      </main>

      <Dialog
        open={isKwSelectionDialogOpen}
        onClose={handleCloseKwSelectionDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Wybierz wpis KW dla MRN</DialogTitle>
        <DialogContent dividers>
          {kwMatches.map((entry) => (
            <div
              key={entry.id}
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                padding: "12px",
                marginBottom: "10px",
              }}
            >
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                ID wpisu KW: {entry.id}
              </Typography>
              <Typography variant="body2">MRN: {entry.mrn || "-"}</Typography>
              <Typography variant="body2">
                Nr akt: {entry.nrAkt || "-"}
              </Typography>
              <Typography variant="body2">
                Status: {entry.status || "-"}
              </Typography>
              <Typography variant="body2">
                Utworzono:{" "}
                {entry.createdAt
                  ? moment(entry.createdAt).format("YYYY-MM-DD HH:mm")
                  : "-"}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                style={{ marginTop: "10px" }}
                onClick={() => {
                  handleCloseKwSelectionDialog();
                  navigateToKwDetail(entry.id);
                }}
              >
                Przejdź do tego wpisu KW
              </Button>
            </div>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseKwSelectionDialog} color="secondary">
            Anuluj
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default EditWniosek1;
